import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/lib/api/sms-lifecycle";

const COHORTS_KEY = "sms-lifecycle-cohorts";
const STEPS_KEY = "sms-lifecycle-steps";
const SETTINGS_KEY = "sms-lifecycle-settings";
const AUDIT_KEY = "sms-lifecycle-audit";
const COHORT_STATS_KEY = "sms-lifecycle-cohort-stats";
const COHORT_SIZE_HISTORY_KEY = "sms-lifecycle-cohort-size-history";

// COHORTS
export const useCohorts = () =>
  useQuery({ queryKey: [COHORTS_KEY], queryFn: api.listCohorts });

export const useCreateCohort = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: api.createCohort,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] });
      toast({ title: "Cohort created" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to create cohort", variant: "destructive" });
    },
  });
};

export const useUpdateCohort = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Parameters<typeof api.updateCohort>[1]) =>
      api.updateCohort(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to update cohort", variant: "destructive" });
    },
  });
};

export const useReorderCohorts = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: api.reorderCohorts,
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: [COHORTS_KEY] });
      const prev = queryClient.getQueryData<api.Cohort[]>([COHORTS_KEY]);
      if (prev) {
        const map = new Map(prev.map((c) => [c.id, c]));
        const reordered = orderedIds
          .map((id, i) => {
            const cohort = map.get(id);
            if (!cohort) return null;
            return { ...cohort, priority: i + 1 };
          })
          .filter((c): c is api.Cohort => c !== null);
        queryClient.setQueryData<api.Cohort[]>([COHORTS_KEY], reordered);
      }
      return { prev };
    },
    onError: (_error: Error, _, ctx) => {
      queryClient.setQueryData([COHORTS_KEY], ctx?.prev);
      toast({ title: "Failed to reorder cohorts", variant: "destructive" });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] }),
  });
};

export const useDeleteCohort = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: api.deleteCohort,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] });
      toast({ title: "Cohort deleted" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to delete cohort", variant: "destructive" });
    },
  });
};

export const useUnlockCohort = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: api.unlockCohort,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] });
      toast({ title: "Cohort unlocked" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to unlock cohort", variant: "destructive" });
    },
  });
};

// STEPS
export const useSteps = (cohortId: string | null) =>
  useQuery({
    queryKey: [STEPS_KEY, cohortId],
    queryFn: () => api.listSteps(cohortId!),
    enabled: !!cohortId,
  });

export const useAddStep = (cohortId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: Parameters<typeof api.addStep>[1]) => api.addStep(cohortId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STEPS_KEY, cohortId] });
      // stepCount on parent cohort changes — keep it fresh
      queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] });
      toast({ title: "Step added" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to add step", variant: "destructive" });
    },
  });
};

export const useUpdateStep = (cohortId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Parameters<typeof api.updateStep>[1]) =>
      api.updateStep(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STEPS_KEY, cohortId] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to update step", variant: "destructive" });
    },
  });
};

export const useDeleteStep = (cohortId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: api.deleteStep,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STEPS_KEY, cohortId] });
      // stepCount on parent cohort changes — keep it fresh
      queryClient.invalidateQueries({ queryKey: [COHORTS_KEY] });
      toast({ title: "Step deleted" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to delete step", variant: "destructive" });
    },
  });
};

// SETTINGS
export const useSettings = () =>
  useQuery({ queryKey: [SETTINGS_KEY], queryFn: api.getSettings });

export const useUpdateSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: api.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY] });
      toast({ title: "Settings saved" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to save settings", variant: "destructive" });
    },
  });
};

// OBSERVABILITY
export const useCohortStats = (params?: Parameters<typeof api.getCohortStats>[0]) =>
  useQuery({
    queryKey: [COHORT_STATS_KEY, params],
    queryFn: () => api.getCohortStats(params),
  });

export const useCohortSizeHistory = (days = 14) =>
  useQuery({
    queryKey: [COHORT_SIZE_HISTORY_KEY, days],
    queryFn: () => api.getCohortSizeHistory({ days }),
  });

export const useAuditTrace = (params?: Parameters<typeof api.getAuditTrace>[0]) =>
  useQuery({
    queryKey: [AUDIT_KEY, params],
    queryFn: () => api.getAuditTrace(params),
  });
